/**
 * The Overview tab: the investigation's own reading, in the app.
 *
 * The study found the best reading of an investigation split three ways — the
 * Breakthroughs tab, which opened on a scan's fifteen dials before any scan
 * existed; the lab's findings brief, behind an instrument header; and the export
 * — and that none of them was where a reader looked first. This tab is the
 * export's brief, read in place: `reportDocument`, the one derivation the
 * Markdown and the PDF already dress, set by `reportBlocks.tsx`. Nothing here
 * writes a sentence about the graph; a section, an order or a word changed in
 * the report changes here too, because there is only the one.
 *
 * WHAT THE APP DOES DIFFERENTLY, and only this:
 *
 *  - It opens on the question, the counts and the lede. Every section is a fold,
 *    shut, with its own first sentence (or its sub-headings) as the preview —
 *    the report's words, so a shut fold still says where that part stands.
 *    Which folds a reader opened is kept per exploration for the life of the
 *    tab, like the thread's seed latch, and not across a reload: the tab's
 *    promise is to open on the lede.
 *  - The report's printed candidate cards give way to the live connections
 *    section (`Breakthroughs.tsx`), seated right after the takeaways they
 *    belong to (the section with id 'insights'): the same pairs, with the
 *    presses that make, search, build and dismiss them. Printing both would
 *    list every pair twice, one of them inert. It is open by default while a
 *    candidate stands, and a scan that lands here, or a question about a pair,
 *    opens it.
 *  - The Insights section (id 'conjectures'; the reader's word since D333)
 *    keeps the ledger's filters — not searched for prior art, not found by
 *    this search, no result recorded yet, result recorded — over its cards.
 *    A filter hides a card whole, by the node the
 *    card names (`Block.node`), and never changes a word of it: the cards are
 *    the report's, so the tab and the documents cannot say two things about one
 *    conjecture (D288). It was the ledger's own component until then, and
 *    printed its own sentences; what the ledger's card said a conjecture had
 *    been through is the report's card's to say now (D291).
 *  - A section can be opened from outside: a line of a run's end card opens the
 *    section it counts ("Held up" → "What held up") with its fold open and its
 *    heading in view (`reportLink.ts`). The report's section id is the anchor.
 *  - The appendix is one fold at the end. Its calibration tables read every
 *    stored exploration, so they are computed when it is opened and not on
 *    every render of a tab that mostly never opens it — the export reads the
 *    same tables at its press, for the same reason, through the same call
 *    (`libraryLedger`), which reads this exploration live: a result recorded a
 *    moment ago is in the table here as it is in the Library's.
 *
 * It reads; it never spends. The one press here that calls a model is the
 * connections section's own, behind its own button (invariant 9).
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { useStore, type ScanNote } from '../core/store'
import {
  CANDIDATES_HEADING,
  CONJECTURES_SECTION,
  reportDocument,
  type Block,
  type ReportOptions,
  type ReportSection,
} from '../core/reportSections'
import { libraryLedger, liveLedgerKey } from '../core/persist'
import { CONJECTURE_FILTER_LABEL, STATUS_WORD } from '../core/vocabulary'
import { onRenamed } from '../core/rename'
import { latestPriorArt, priorArtLive } from '../core/priorArt'
import type { ReasonNode, Session } from '../core/types'
import { Breakthroughs, CONNECTIONS_LEDE, hasScanned } from './Breakthroughs'
import { useReportSectionAsk } from './reportLink'
import { Counts, ReportBlocks, RichText, plainText } from './reportBlocks'
import { Icon } from './components/Icon'
import './Overview.css'

/** The live sections' ids among the report's. Not `reportDocument`'s. */
const CONNECTIONS = 'connections'
const APPENDIX = 'appendix'

/** The fold states a reader set, per exploration, for the life of the tab. The
 *  tab unmounts whenever the Thread tab is chosen, and a fold that shut itself
 *  on every return would be a fold nobody could keep open. */
const foldLatch = new Map<string, Map<string, boolean>>()
// The one rename that is not a switch: a paid pass on the worked example remints
// its id (`core/rename.ts`). The document on screen did not change, so neither
// do its folds — without this, pressing Find connections on the example shut
// the very section the press was made in.
onRenamed(({ from, to }) => {
  const m = foldLatch.get(from)
  if (!m) return
  foldLatch.set(to, m)
  foldLatch.delete(from)
})

/** Scan notes whose landing this tab has already brought on screen. A note is
 *  written once per scan that lands (`noteScan`), in memory only, so after a
 *  reload nothing is revealed that the reader did not ask to see. */
const revealed = new WeakSet<ScanNote>()

/**
 * A shut section's one line: its first paragraph, which in every section of the
 * report is the sentence that says where that part stands ("Nothing in this
 * exploration has been tested."), or where a section opens on sub-headings, the
 * sub-headings. The report's own words either way.
 */
export function previewOf(blocks: Block[]): string | null {
  const first = blocks[0]
  if (first?.b === 'para') return plainText(first.rich)
  const heads = blocks.flatMap((b) => (b.b === 'h3' ? [b.text] : []))
  return heads.length ? heads.join(' · ') : null
}

/**
 * The takeaway and synthesis section (id 'insights') less the printed
 * candidates, which the live connections section stands in for. Cut at the
 * report's own heading for them, so nothing above it can be lost to the cut and
 * nothing below it can survive it.
 */
export function withoutCandidates(section: ReportSection): ReportSection {
  const at = section.blocks.findIndex((b) => b.b === 'h3' && b.text === CANDIDATES_HEADING)
  return at < 0 ? section : { ...section, blocks: section.blocks.slice(0, at) }
}

export type ConjectureFilter = keyof typeof CONJECTURE_FILTER_LABEL

/** The ledger's filters (D279, D282), in the words the cards under them print,
 *  from the status-word table (D166, D287). Recorded facts only: "Not found by
 *  this search" is a reading a search wrote, never a merit — a list of places to
 *  look. */
export { CONJECTURE_FILTER_LABEL }

/** Whether a conjecture passes a filter, off its stored records. A search of
 *  wording since rewritten is no search of these words. */
export function conjectureShown(filter: ConjectureFilter, n: ReasonNode): boolean {
  const art = latestPriorArt(n)
  const live = art !== undefined && priorArtLive(n, art)
  switch (filter) {
    case 'all':
      return true
    case 'unsearched':
      return !live
    case 'not-found':
      return live && art.reading === 'not-found'
    case 'no-result':
      return !n.outcome
    case 'result':
      return !!n.outcome
  }
}

/**
 * The conjectures section as a filter shows it: every card whose node passes,
 * whole, and every other block as the report has it. Hides; never rewords.
 */
export function filterConjectureCards(
  blocks: Block[],
  session: Session,
  filter: ConjectureFilter,
): Block[] {
  return blocks.filter((b) => {
    if (b.b !== 'card' || b.node === undefined) return true
    const n = session.nodes[b.node]
    return !n || conjectureShown(filter, n)
  })
}

/**
 * How many cards each filter would show — counted off the same reading
 * `filterConjectureCards` hides by, so a button's number is the number of cards
 * its press leaves (D335).
 */
export function conjectureCounts(
  blocks: Block[],
  session: Session,
): Record<ConjectureFilter, number> {
  const out = Object.fromEntries(
    (Object.keys(CONJECTURE_FILTER_LABEL) as ConjectureFilter[]).map((f) => [f, 0]),
  ) as Record<ConjectureFilter, number>
  for (const b of blocks) {
    if (b.b !== 'card' || b.node === undefined) continue
    const n = session.nodes[b.node]
    for (const f of Object.keys(out) as ConjectureFilter[]) {
      if (!n || conjectureShown(f, n)) out[f]++
    }
  }
  return out
}

/**
 * The Insights fold: the report's section, with the ledger's filters over its
 * cards. The filters sit under the section's opening sentences and above the
 * first card, where the ledger had them, and only where there is a card to hide.
 */
function ConjecturesFold({
  section,
  session,
  open,
  onToggle,
}: {
  section: ReportSection
  session: Session
  open: boolean
  onToggle: () => void
}) {
  const [filter, setFilter] = useState<ConjectureFilter>('all')
  const cards = section.blocks.filter((b) => b.b === 'card' && b.node !== undefined).length
  const first = section.blocks.findIndex((b) => b.b === 'card')
  const intro = first < 0 ? section.blocks : section.blocks.slice(0, first)
  const rest = first < 0 ? [] : filterConjectureCards(section.blocks.slice(first), session, filter)
  const none = cards > 0 && !rest.some((b) => b.b === 'card')
  const counts = conjectureCounts(section.blocks, session)
  return (
    <Section
      id={section.id}
      title={section.title}
      preview={previewOf(section.blocks)}
      meta={cards ? String(cards) : undefined}
      open={open}
      onToggle={onToggle}
    >
      <ReportBlocks blocks={intro} level={4} />
      {cards > 0 && (
        <div className="ovw-filters" role="group" aria-label="Show insights">
          {/* Each with how many cards it would show (D335), so no press lands on
              "None under this filter", and "Result recorded 2" is how close the
              section is. A filter with none is shut but kept, so the row keeps
              its shape — unless it is the one on, which must stay pressable. */}
          {(Object.keys(CONJECTURE_FILTER_LABEL) as ConjectureFilter[]).map((f) => {
            const shut = counts[f] === 0 && filter !== f
            return (
              <button
                key={f}
                type="button"
                className={filter === f ? 'btn btn--on' : 'btn btn--outline'}
                aria-pressed={filter === f}
                aria-disabled={shut || undefined}
                title={shut ? 'None yet' : undefined}
                onClick={() => !shut && setFilter(f)}
              >
                {/* The space inside the count, so the label is the button's
                    first text whole and the name reads "Result recorded 2"; the
                    `.btn`'s own gap draws it. */}
                {CONJECTURE_FILTER_LABEL[f]}
                <span className="num" style={{ opacity: 0.7 }}>
                  {' '}
                  {counts[f]}
                </span>
              </button>
            )
          })}
        </div>
      )}
      {none && (
        <p className="rb-para" data-tone="quiet">
          None under this filter.
        </p>
      )}
      <ReportBlocks blocks={rest} level={4} />
    </Section>
  )
}

function Section({
  id,
  title,
  preview,
  meta,
  open,
  onToggle,
  sectionRef,
  children,
}: {
  /** The report's section id, the anchor a deep link lands on (`reportLink.ts`). */
  id?: string
  title: string
  preview: string | null
  meta?: string
  open: boolean
  onToggle: () => void
  sectionRef?: RefObject<HTMLElement | null>
  children: ReactNode
}) {
  const bodyId = useId()
  const titleId = useId()
  const previewId = useId()
  const metaId = useId()
  const previewing = !open && !!preview
  return (
    <section className="ovw-sec" data-open={open || undefined} data-sec={id} ref={sectionRef}>
      <h3 className="ovw-sec-h">
        {/* The whole line is the press, and it says what is inside while shut —
            the card's fold rule (`Fold` in NodeCard.tsx). The body is hidden,
            never unmounted, so `aria-controls` always names an element, and the
            connections section's own effects run while it is shut. */}
        <button
          type="button"
          className="ovw-sec-head"
          aria-expanded={open}
          aria-controls={bodyId}
          // Named by its title (and its count), described by the preview: the
          // whole line as the name made each heading a paragraph long, and a
          // screen reader's list of headings read as the report itself.
          aria-labelledby={meta ? `${titleId} ${metaId}` : titleId}
          aria-describedby={previewing ? previewId : undefined}
          onClick={onToggle}
        >
          <Icon
            name="chevron"
            size={9}
            style={{
              flex: 'none',
              marginTop: 5,
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
          <span className="ovw-sec-text">
            <span id={titleId} className="ovw-sec-title">
              {title}
            </span>
            {previewing && (
              <span id={previewId} className="ovw-sec-preview">
                {preview}
              </span>
            )}
          </span>
          {meta && (
            <span id={metaId} className="ovw-sec-meta">
              {meta}
            </span>
          )}
        </button>
      </h3>
      <div className="ovw-sec-body" id={bodyId} hidden={!open}>
        {children}
      </div>
    </section>
  )
}

export function Overview() {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const lens = useStore((s) => s.lens)
  const temper = useStore((s) => s.temper)
  const chat = useStore((s) => s.chat)
  const note = useStore((s) => s.lastScan[s.session.id])
  const scanning = useStore((s) => s.busy && s.activeOp === 'bridge')

  // The fold states, read through the latch and written through it; `bump`
  // only asks for the render that reads the write.
  const [, bump] = useState(0)
  const latch = foldLatch.get(session.id)
  const setOpen = useCallback(
    (id: string, open: boolean) => {
      let m = foldLatch.get(session.id)
      if (!m) foldLatch.set(session.id, (m = new Map()))
      m.set(id, open)
      bump((n) => n + 1)
    },
    [session.id],
  )

  const pairs = session.bridges.length
  const scanned = hasScanned(session) || note !== undefined
  // Open by default while a candidate stands — it is what the old tab opened
  // on. The reader's own press wins after that, either way.
  const connOpen = latch?.get(CONNECTIONS) ?? pairs > 0
  const appendixOpen = latch?.get(APPENDIX) ?? false
  const connRef = useRef<HTMLElement>(null)

  // A conversation about a pair renders under its card and nowhere else, so a
  // question pinned to one opens the section it lives in — on the pin, once,
  // so the fold still shuts when the reader shuts it.
  const bridgeChat = chat?.kind === 'bridge' ? chat.id : null
  useEffect(() => {
    if (bridgeChat !== null) setOpen(CONNECTIONS, true)
  }, [bridgeChat, setOpen])

  const revealConnections = useCallback(() => {
    setOpen(CONNECTIONS, true)
    requestAnimationFrame(() =>
      connRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
    )
  }, [setOpen])

  // A line of a run's end card asked for one section (`reportLink.ts`): its fold
  // open, brought into view, and — where the press that asked lost its focus to
  // the tab switch — focus on the section's own heading, so the next key lands in
  // what was opened rather than at the top of the page. An instant jump under
  // reduced motion, as the run account's landing does it.
  const root = useRef<HTMLDivElement>(null)
  const land = useCallback(
    (id: string) => {
      setOpen(id, true)
      requestAnimationFrame(() => {
        const sec = root.current?.querySelector<HTMLElement>(`[data-sec="${id}"]`)
        if (!sec) return
        const still =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        sec.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' })
        const here = document.activeElement
        if (!here || here === document.body) {
          sec.querySelector<HTMLElement>('.ovw-sec-head')?.focus({ preventScroll: true })
        }
      })
    },
    [setOpen],
  )
  useReportSectionAsk(session.id, land)

  // A scan that landed while this tab was showing, or that switched the pane
  // to it, is brought on screen once: the pairs it proposed are what somebody
  // pressed for. Once per scan — the note is the scan's — so returning to the
  // tab later opens on the lede as it always does.
  useEffect(() => {
    if (!note || revealed.has(note)) return
    revealed.add(note)
    if (pairs > 0) revealConnections()
  }, [note, pairs, revealConnections])

  // The library-wide tables only once the appendix is open; see the header. This
  // exploration is read live (`libraryLedger`), so the key is what it puts into
  // the tables (`liveLedgerKey`) rather than every edit: a result, a check or a
  // panel landing here moves them, and one more node does not.
  const ledgerKey = appendixOpen ? liveLedgerKey(session) : ''
  const tables = useMemo<ReportOptions>(
    () => (appendixOpen ? libraryLedger(useStore.getState().session) : {}),
    [appendixOpen, session.id, ledgerKey],
  )
  const doc = useMemo(
    () => reportDocument(session, { lens, temper, ...tables }),
    // `rev` because a patch can land on a node without a new session object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, rev, lens, temper, tables],
  )

  const connMeta = scanning
    ? STATUS_WORD.running
    : scanned
      ? `${pairs} pair${pairs === 1 ? '' : 's'}`
      : STATUS_WORD.notScanned

  const connections = (
    <Section
      key={CONNECTIONS}
      title="Connections between branches"
      preview={CONNECTIONS_LEDE}
      meta={connMeta}
      open={connOpen}
      onToggle={() => setOpen(CONNECTIONS, !connOpen)}
      sectionRef={connRef}
    >
      <Breakthroughs onReveal={revealConnections} />
    </Section>
  )

  return (
    <div className="insp scroll ovw" ref={root}>
      <header className="ovw-head">
        <h2 className="rb-title">{doc.title}</h2>
        <Counts items={doc.header} />
        <p className="rb-lede">
          <RichText rich={[{ text: doc.lede }]} />
        </p>
        {doc.preamble.length > 0 && (
          <div className="ovw-preamble">
            <ReportBlocks blocks={doc.preamble} level={4} />
          </div>
        )}
      </header>

      <div className="ovw-secs">
        {doc.sections.flatMap((raw) => {
          // A trap since D333: the section with id 'insights' is the TAKEAWAYS.
          // The fold a reader sees titled "Insights" is `CONJECTURES_SECTION`.
          // The ids stay, because a deep link (`reportLink.ts`) lands on them.
          const sec = raw.id === 'insights' ? withoutCandidates(raw) : raw
          const open = latch?.get(sec.id) ?? false
          // The report's own section, which comes after the takeaways (id
          // 'insights') and so after the connections seated there — where the
          // ledger sat (D284).
          // Every graph has the fold now, as every report has the section
          // (D141): an empty one says so in a sentence, and gives no
          // instruction, which belongs on the press.
          if (sec.id === CONJECTURES_SECTION) {
            return [
              <ConjecturesFold
                key={sec.id}
                section={sec}
                session={session}
                open={open}
                onToggle={() => setOpen(sec.id, !open)}
              />,
            ]
          }
          const node = (
            <Section
              key={sec.id}
              id={sec.id}
              title={sec.title}
              preview={previewOf(sec.blocks)}
              open={open}
              onToggle={() => setOpen(sec.id, !open)}
            >
              <ReportBlocks blocks={sec.blocks} level={4} />
            </Section>
          )
          return sec.id === 'insights' ? [node, connections] : [node]
        })}
        {/* A report without the takeaway section (id 'insights') would
            otherwise lose the live one with it; today every report has one. */}
        {!doc.sections.some((x) => x.id === 'insights') && connections}

        <Section
          title="Appendix"
          preview={doc.appendixIntro}
          open={appendixOpen}
          onToggle={() => setOpen(APPENDIX, !appendixOpen)}
        >
          <p className="rb-para" data-tone="quiet">
            {doc.appendixIntro}
          </p>
          {doc.appendix.map((x) => (
            <div key={x.id} className="ovw-appx">
              {/* The ledger prints its own title, as `reportBody` has it. */}
              {x.blocks[0]?.b !== 'ledger' && <h4 className="rb-h2">{x.title}</h4>}
              <ReportBlocks blocks={x.blocks} level={5} />
            </div>
          ))}
        </Section>
      </div>
    </div>
  )
}
